"use client";

import { Play, Pause, ListVideo } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { HorizontalVideoCardProps } from "./HorizontalVideoCard.types";
import VideoCardSkeleton from "../../skeletons/VideoCardSkeleton/VideoCardSkeleton";
import PrivateCdnImage from "@/asset-delivery/components/PrivateCdnImage";

import "./HorizontalVideoCard.css";

const HorizontalVideoCard = ({
  title,
  videoFile,
  thumbnail,
  thumbnailTarget,
  thumbnailKind = "thumbnail",
  thumbnailFieldName = "thumbnail",
  showPlaylistIcon = false,
  onPlaylistClick,
  className = "",
  onClick,
  iconSize = 28,
  isPlaceholder = false,
}: HorizontalVideoCardProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTitleExpanded, setIsTitleExpanded] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const toggleTitle = () => setIsTitleExpanded((prev) => !prev);

  const handleTogglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleEnded = () => setIsPlaying(false);
    video.addEventListener("ended", handleEnded);

    return () => video.removeEventListener("ended", handleEnded);
  }, []);

  if (isPlaceholder) {
    return (
      <div className={`horizontal-card-container ${className} placeholder`}>
        <div className="horizontal-thumbnail placeholder-bg">
          <div className="placeholder-glow-circle">
            <Play size={iconSize * 1.5} />
          </div>
          <div className="placeholder-message">
            <p className="placeholder-title">Coming Soon</p>
            <p className="placeholder-subtitle">This space is becoming sacred</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`horizontal-card-container ${className}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="horizontal-thumbnail">
        {!isLoaded && (
          <>
            <VideoCardSkeleton variant="horizontal" />

            {/* ✅ CDN-only thumbnail warmup */}
            {thumbnail && thumbnailTarget && (
              <div style={{ display: "none" }} aria-hidden="true">
                <PrivateCdnImage
                  src={thumbnail}
                  placeholderSrc={null}
                  alt="poster preloader"
                  target={thumbnailTarget}
                  kind={thumbnailKind as any}
                  fieldName={thumbnailFieldName as any}
                  rootMargin="900px"
                  threshold={0.01}
                  failOpen
                  imgLoading="eager"
                  fetchPriority="high"
                  onLoad={() => setIsLoaded(true)}
                  onError={() => {
                    // keep skeleton until video loads
                  }}
                />
              </div>
            )}

            {/* Optional fallback if no target passed */}
            {thumbnail && !thumbnailTarget && (
              <img
                src={thumbnail}
                alt="poster preloader"
                style={{ display: "none" }}
                onLoad={() => setIsLoaded(true)}
              />
            )}
          </>
        )}

        <video
          ref={videoRef}
          src={videoFile}
          poster={thumbnail}
          muted
          playsInline
          preload="metadata"
          className={`horizontal-video-element ${isLoaded ? "loaded" : "loading"}`}
          onLoadedData={() => setIsLoaded(true)}
        />

        <button
          className="horizontal-play-button"
          onClick={(e) => {
            e.stopPropagation();
            handleTogglePlay();
          }}
          aria-label={isPlaying ? "Pause video" : "Play video"}
        >
          {isPlaying ? (
            <Pause size={iconSize} className="horizontal-play-icon" />
          ) : (
            <Play size={iconSize} className="horizontal-play-icon" />
          )}
        </button>

        {title && (
          <div
            className={`horizontal-title-overlay ${
              isTitleExpanded ? "expanded" : "collapsed"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              toggleTitle();
            }}
            title={isTitleExpanded ? "Hide Title" : "Show Full Title"}
          >
            <p>{title}</p>
          </div>
        )}
      </div>

      {showPlaylistIcon && onPlaylistClick && (
        <button
          className="horizontal-playlist-icon"
          onClick={(e) => {
            e.stopPropagation();
            onPlaylistClick();
          }}
          title="Go to Playlist"
        >
          <ListVideo size={iconSize} />
        </button>
      )}
    </div>
  );
};

export default HorizontalVideoCard;
